import { createSlice } from "@reduxjs/toolkit";

import { LoggedUser, ISelectedPatient, emptyLoggedUser, emptySelectedPatient } from "./interfaces";
import { AreaItem, HeaderState, HospitalItem } from "../components/Header/HeaderUtils";
import { OptionCategory } from "../interfaces/OptionCategory";
import { PageState } from "../views/Home/HomeUtils";

/**
 * Interfaz para AppState.
 *
 * Contiene:
 *  - loggedUser:                Información del usuario autenticado.
 *  - selectedPatient:           Información de un paciente asignado o no asignado.
 *  - categoryList:              Información de la lista de elementos seleccionados de la categoría. Usada para
 * 									asignar un paciente.
 *  - isAuthenticated:           Usado para verifica si puede acceder a las distintas rutas.
 *  - viewFullCalendar:          Usado para manejar los estados de los calendarios en Ajuste de Asistencia.
 *  - headerState:               Usado para manejar los estados del Header entre las distintas pantallas.
 *  - previousPageState:		 Usado para almacenar el estado previo del PageState cuando regresa de Asignación Diaria
 *  - pageState:                 Usado para manejar qué pantalla se muestra.
 *  - selectedAreaId:            Usado en el Header para poder filtrar las enfermeras en Asignación Diaria.
 *  - isPatientsEnabled:         Usado para permitir la selección de la pantalla de Pacientes.
 *  - triggerOnboarding:         Usado para activar el Onboarding.
 *  - triggerDownloadPDF:        Usado para activar la descarga del PDF de Asignación Diaria.
 *  - isDownloadPDFButtonEnable: Usado para activar o desactivar el botón de Descargar PDF en Header.
 *  - selectedArea: 			 Usado para guardar el Area seleccionada en Header.
 *  - selectedHospital:  		 Usado para guardar el Hospital seleccionado en Header.
 */
export interface AppState {
	loggedUser: LoggedUser;
	selectedPatient: ISelectedPatient;
	categoryList: OptionCategory[];
	isAuthenticated: boolean;
	viewFullCalendar: boolean;
	headerState: HeaderState;
	previousPageState: PageState;
	pageState: PageState;
	selectedAreaId: string;
	isPatientsEnabled: boolean;
	triggerOnboarding: boolean;
	triggerDownloadPDF: boolean;
	isDownloadPDFButtonEnable: boolean;
	selectedArea?: AreaItem;
	selectedHospital?: HospitalItem;
}

const initialState: AppState = {
	loggedUser: emptyLoggedUser,
	selectedPatient: emptySelectedPatient,
	categoryList: [],
	isAuthenticated: JSON.parse(localStorage.getItem("isAuthenticatedNurasign") || "false"),
	viewFullCalendar: false,
	headerState: HeaderState.Normal,
	previousPageState: PageState.Patients,
	pageState: PageState.Patients,
	selectedAreaId: "",
	isPatientsEnabled: false,
	triggerOnboarding: true,
	triggerDownloadPDF: false,
	isDownloadPDFButtonEnable: false,
};

const appStateSlide = createSlice({
	name: "appState",
	initialState,
	reducers: {
		setLoggedUser(state, action) {
			state.loggedUser = action.payload;
		},

		deleteLoggedUser(state) {
			state.loggedUser = emptyLoggedUser;
		},

		setSelectedPatient(state, action) {
			state.selectedPatient = action.payload;
		},

		deleteSelectedPatient(state) {
			state.selectedPatient = emptySelectedPatient;
		},

		setPatientCategoryId(state, action) {
			state.selectedPatient.categoryId = action.payload;
		},

		deletePatientCategoryId(state) {
			state.selectedPatient.categoryId = null;
		},

		setPatientNurse(state, action) {
			state.selectedPatient.nurse_id = action.payload.nurse_id;
			state.selectedPatient.nurseName = action.payload.nurseName;
		},

		deletePatientNurse(state) {
			state.selectedPatient.nurse_id = null;
			state.selectedPatient.nurseName = null;
		},

		setPatientSecondaryNurse(state, action) {
			state.selectedPatient.secondarynurse_id = action.payload.secondarynurse_id;
			state.selectedPatient.secondaryNurseName = action.payload.secondaryNurseName;
		},

		deletePatientSecondaryNurse(state) {
			state.selectedPatient.secondarynurse_id = null;
			state.selectedPatient.secondaryNurseName = null;
		},

		setCategoryList(state, action) {
			state.categoryList = action.payload;
		},

		deleteCategoryList(state) {
			state.categoryList = [];
		},

		setIsAuthenticated(state, action) {
			localStorage.setItem("isAuthenticatedNurasign", JSON.stringify(action.payload));
			state.isAuthenticated = action.payload;
		},

		setViewFullCalendar(state, action) {
			state.viewFullCalendar = action.payload;
		},

		setHeaderState(state, action) {
			state.headerState = action.payload;
		},

		setPageState(state, action) {
			state.previousPageState = state.pageState;
			state.pageState = action.payload;
		},

		setSelectedAreaId(state, action) {
			state.selectedAreaId = action.payload;
		},

		setIsPatientsEnabled(state, action) {
			state.isPatientsEnabled = action.payload;
		},

		setTriggerOnboarding(state, action) {
			state.triggerOnboarding = action.payload;
		},

		setTriggerDownloadPDF(state, action) {
			state.triggerDownloadPDF = action.payload;
		},

		setIsDownloadPDFButtonEnable(state, action) {
			state.isDownloadPDFButtonEnable = action.payload;
		},

		setSelectedArea(state, action) {
			state.selectedArea = action.payload;
		},

		setSelectedHospital(state, action) {
			state.selectedHospital = action.payload;
		},
	},
});

export const {
	setLoggedUser,
	deleteLoggedUser,
	setSelectedPatient,
	deleteSelectedPatient,
	setPatientCategoryId,
	deletePatientCategoryId,
	setPatientNurse,
	deletePatientNurse,
	setPatientSecondaryNurse,
	deletePatientSecondaryNurse,
	setCategoryList,
	deleteCategoryList,
	setIsAuthenticated,
	setViewFullCalendar,
	setHeaderState,
	setPageState,
	setSelectedAreaId,
	setIsPatientsEnabled,
	setTriggerOnboarding,
	setTriggerDownloadPDF,
	setIsDownloadPDFButtonEnable,
	setSelectedArea,
	setSelectedHospital,
} = appStateSlide.actions;

export default appStateSlide.reducer;
